// Makes "a route chunk is downloading" observable to the UI.
//
// Every route in App.tsx is a React.lazy component behind one shared
// <Suspense>. React Router v7 always wraps location updates in
// React.startTransition, and React will not replace already-visible content
// with a Suspense fallback during a transition — so while the next route's
// chunk downloads, the PREVIOUS page stays on screen and the fallback never
// renders. On a slow connection tapping a nav item looks like nothing
// happened.
//
// useNavigation() would report that pending window, but it only exists on a
// data router; council uses declarative <BrowserRouter>, where NavLink's
// isPending is always false. useLocation() cannot see it either: the location
// is the state being updated inside the transition, so it does not commit
// until the chunk has already landed — by then the wait is over.
//
// So the signal is taken from the thing that is actually slow: the dynamic
// import itself.

type Listener = () => void;

const pendingPaths = new Set<string>();
const listeners = new Set<Listener>();

// React.lazy invokes its factory during the render phase, so notifying
// subscribers synchronously would be a setState-during-render. Coalesce into a
// microtask, which runs after the render pass and before paint.
let notifyQueued = false;

function notify() {
  if (notifyQueued) return;
  notifyQueued = true;
  queueMicrotask(() => {
    notifyQueued = false;
    for (const listener of listeners) listener();
  });
}

export function subscribeRouteLoading(listener: Listener) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** True while any route chunk is in flight. Stable enough for useSyncExternalStore. */
export function isRouteLoading() {
  return pendingPaths.size > 0;
}

/** True while the chunk backing `path` is in flight. */
export function isPathLoading(path: string) {
  return pendingPaths.has(path);
}

/**
 * Wrap a route's dynamic import so its download is visible to the UI.
 *
 * `path` is the route the chunk backs, which lets a NavLink pointing at that
 * path show its own pending state. React.lazy caches the factory result, so
 * this runs once per chunk — on the first navigation that needs it, which is
 * the only navigation that stalls.
 */
export function trackRouteChunk<T>(path: string, load: () => Promise<T>): Promise<T> {
  pendingPaths.add(path);
  notify();
  return load().finally(() => {
    pendingPaths.delete(path);
    notify();
  });
}
